// Simplified psychrometric helpers for client-side computation. Used to
// compute Primary-Air sensible + latent CAPACITY contribution to the room
// for the PA-aware Detailed Selection view.
//
// All temperatures in °F, CFM at sea-level air density (uncorrected — for
// altitude correction the backend's psychrolib remains authoritative; this
// is an at-a-glance UI computation, not the authoritative selection math).
//
// References: ASHRAE Fundamentals coefficients
//   Q_sensible = 1.08 × CFM × ΔT          [BTU/h]
//   Q_latent   = 4840 × CFM × Δw          [BTU/h, w = lb/lb humidity ratio]
// Humidity ratio approximation via Antoine / Hyland-Wexler simplified.

const C_SENS = 1.08;
const C_LAT_LB = 4840;
const ATM_PSI = 14.696;

// Saturation pressure of water (psi) — Magnus (Antoine) form, ±0.3% over 32–150°F.
function psat_psi(T_F) {
  const T_C = (T_F - 32) * 5 / 9;
  const Pa = 610.78 * Math.exp((17.27 * T_C) / (T_C + 237.3));
  return Math.max(0, Pa / 6894.76);  // Pa → psi
}

// Humidity ratio (lb water / lb dry air) given T (°F) + RH (%).
export function humidityRatio(T_F, RH_pct) {
  const rh = Math.max(0, Math.min(100, RH_pct || 0)) / 100;
  const Pw = rh * psat_psi(T_F);
  return (0.622 * Pw) / Math.max(ATM_PSI - Pw, 0.001);
}

// Dewpoint temperature (°F) given T (°F) + RH (%).
export function dewpointF(T_F, RH_pct) {
  if (!RH_pct || RH_pct <= 0) return -459;
  const T_C = (T_F - 32) * 5 / 9;
  const a = 17.27, b = 237.3;
  const alpha = (a * T_C) / (b + T_C) + Math.log(Math.max(RH_pct / 100, 0.0001));
  const dp_C = (b * alpha) / (a - alpha);
  return dp_C * 9 / 5 + 32;
}

// Primary-Air sensible CAPACITY contribution to room (BTU/h).
//   positive: PA cools the room (PA colder than room)
//   negative: PA loads the room (PA warmer than room — e.g. heating mode)
export function paSensibleCapacity(paCfm, T_room_F, T_pa_F) {
  if (!paCfm || paCfm <= 0) return 0;
  return C_SENS * paCfm * (T_room_F - T_pa_F);
}

// Primary-Air latent CAPACITY contribution to room (BTU/h).
//   positive: PA dehumidifies room (PA drier than room)
//   negative: PA humidifies room
export function paLatentCapacity(paCfm, T_room_F, RH_room_pct, T_pa_F, RH_pa_pct) {
  if (!paCfm || paCfm <= 0) return 0;
  const w_room = humidityRatio(T_room_F, RH_room_pct);
  const w_pa = humidityRatio(T_pa_F, RH_pa_pct);
  return C_LAT_LB * paCfm * (w_room - w_pa);
}
